import { apiRequest } from "@/lib/queryClient";
import type { Client, ClientContact, InsertClientContact } from "@shared/schema";

export interface ClientsResponse {
    success: boolean;
    data: {
        clients: (Client & { advertiserCount?: number })[];
        pagination: {
            limit: number;
            offset: number;
            total: number;
            hasMore: boolean;
        };
    };
    message: string;
}

export interface ClientDetailResponse {
    success: boolean;
    data: {
        client: Client & {
            advertiserCount: number;
            advertisers: { id: number; name: string; isActive: boolean }[];
        };
    };
    message: string;
}

export interface CreateClientDTO {
    name: string;
    description?: string;
    billingName?: string;
    billingAddress?: string;
    vatNumber?: string;
    paymentTerms?: string;
    primaryContactName?: string;
    primaryContactEmail?: string;
    primaryContactPhone?: string;
}

export const ClientService = {
    // Get paginated list of clients
    getClients: async (params?: { search?: string; includeInactive?: boolean; limit?: number; offset?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.includeInactive) queryParams.append("includeInactive", "true");
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.offset) queryParams.append("offset", params.offset.toString());

        const res = await apiRequest(`/api/clients?${queryParams.toString()}`, "GET");
        return res.json() as Promise<ClientsResponse>;
    },

    // Get single client by ID
    getClient: async (id: number) => {
        const res = await apiRequest(`/api/clients/${id}`, "GET");
        return res.json() as Promise<ClientDetailResponse>;
    },

    // Create new client
    createClient: async (data: CreateClientDTO) => {
        const res = await apiRequest(
            "/api/clients",
            "POST",
            data
        );
        return res.json() as Promise<{ success: boolean; data: { client: Client }; message: string }>;
    },

    // Update existing client
    updateClient: async (id: number, data: Partial<CreateClientDTO> & { isActive?: boolean }) => {
        const res = await apiRequest(
            `/api/clients/${id}`,
            "PUT",
            data
        );
        return res.json() as Promise<{ success: boolean; data: { client: Client }; message: string }>;
    },

    // Soft delete client
    deleteClient: async (id: number) => {
        const res = await apiRequest(
            `/api/clients/${id}`,
            "DELETE"
        );
        return res.json() as Promise<{ success: boolean; message: string }>;
    },

    // Get client contacts
    getContacts: async (clientId: number) => {
        const res = await apiRequest(`/api/clients/${clientId}/contacts`, "GET");
        return res.json() as Promise<{ success: boolean; data: ClientContact[] }>;
    },

    // Create client contact
    createContact: async (clientId: number, data: Omit<InsertClientContact, 'clientId'>) => {
        const res = await apiRequest(`/api/clients/${clientId}/contacts`, "POST", data);
        return res.json() as Promise<{ success: boolean; data: ClientContact }>;
    },

    // Update client contact
    updateContact: async (contactId: number, data: Partial<Omit<InsertClientContact, 'clientId'>>) => {
        const res = await apiRequest(`/api/contacts/${contactId}`, "PUT", data);
        return res.json() as Promise<{ success: boolean; data: ClientContact }>;
    },

    // Delete (deactivate) client contact
    deleteContact: async (contactId: number) => {
        const res = await apiRequest(`/api/contacts/${contactId}`, "DELETE");
        return res.json() as Promise<{ success: boolean; message: string }>;
    },
};
